import type { AnalyticsBrowser } from "@customerio/cdp-analytics-browser";

import {
  mergeCustomerIoProfiles,
  trackPublicEvent,
} from "@/features/analytics/api";
import type { EventPrefix } from "@/features/analytics/models";
import {
  ANALYTICS_PARAMS,
  ANALYTICS_PROVIDER,
  CUSTOMER_IO_CATEGORY_SLUG,
  CUSTOMER_IO_EVENT,
  EVENT_PREFIX,
} from "@/features/analytics/models";
import { handleAnalyticsError } from "@/features/analytics/utils";
import {
  categoriesToCustomerIoProducts,
  hasWeightLossProduct,
} from "@/features/analytics/utils/customer-io";
import type { OnboardingUser } from "@/features/onboarding/models";
import type { UrlParamsSlice } from "@/features/onboarding/stores";
import type { CheckoutDiscount } from "@/features/payments/api";
import type { CategoryWithSelectedProduct } from "@/features/products/models";
import { calculateTotal } from "@/features/products/utils";
import { removeEmptyValues } from "@/shared/utils";

export interface EverflowAttribution {
  efTransactionId?: string | null;
  sourceId?: string | null;
  uid?: string | null;
  sub1?: string | null;
  sub2?: string | null;
  sub3?: string | null;
  sub4?: string | null;
  sub5?: string | null;
}

interface CustomerIoAffiliateIdCapturedParams {
  analytics: AnalyticsBrowser;
  email: OnboardingUser["email"];
  affiliateId?: string | null;
  everflowAttribution?: EverflowAttribution;
}

export const hasEverflowAttribution = (
  attribution?: EverflowAttribution,
): boolean =>
  Boolean(attribution?.efTransactionId) ||
  Boolean(attribution?.sourceId) ||
  Boolean(attribution?.uid) ||
  Boolean(attribution?.sub1) ||
  Boolean(attribution?.sub2) ||
  Boolean(attribution?.sub3) ||
  Boolean(attribution?.sub4) ||
  Boolean(attribution?.sub5);

const buildEverflowTraits = (attribution?: EverflowAttribution) => ({
  ...(attribution?.efTransactionId && {
    everflowTransactionId: attribution.efTransactionId,
  }),
  ...(attribution?.sourceId && { everflowSourceId: attribution.sourceId }),
  ...(attribution?.uid && { everflowUid: attribution.uid }),
  ...(attribution?.sub1 && { everflowSub1: attribution.sub1 }),
  ...(attribution?.sub2 && { everflowSub2: attribution.sub2 }),
  ...(attribution?.sub3 && { everflowSub3: attribution.sub3 }),
  ...(attribution?.sub4 && { everflowSub4: attribution.sub4 }),
  ...(attribution?.sub5 && { everflowSub5: attribution.sub5 }),
});

export const triggerCustomerIoAffiliateIdCaptured = ({
  analytics,
  email,
  affiliateId,
  everflowAttribution,
}: CustomerIoAffiliateIdCapturedParams) => {
  const everflowTraits = buildEverflowTraits(everflowAttribution);

  void analytics.identify(email, {
    ...(affiliateId && { everflowAffiliateId: affiliateId }),
    ...everflowTraits,
  });
  trackPublicEvent({
    event: CUSTOMER_IO_EVENT.AFFILIATE_ID_CAPTURED,
    properties: {
      ...(affiliateId && { affiliateId }),
      ...everflowTraits,
      source: window.location.href,
    },
    userId: email,
  });
};

interface CustomerIoCategoriesSelected {
  selectedCategories: CategoryWithSelectedProduct[];
  userId?: string;
  eventPrefix?: EventPrefix;
}

export const triggerCustomerIoProductsSelected = ({
  selectedCategories,
  userId,
  eventPrefix = EVENT_PREFIX.NONE,
}: CustomerIoCategoriesSelected) => {
  trackPublicEvent({
    event: `${eventPrefix}${CUSTOMER_IO_EVENT.PRODUCT_SELECTED}`,
    properties: {
      products: selectedCategories.map((category) => ({
        productCategory: CUSTOMER_IO_CATEGORY_SLUG[category.slug],
        productName: category.selectedProduct?.name,
      })),
      source: window.location.href,
    },
    userId,
  });
};

export const triggerCustomerIoPricesSelected = ({
  selectedCategories,
  userId,
  eventPrefix = EVENT_PREFIX.NONE,
}: CustomerIoCategoriesSelected) => {
  trackPublicEvent({
    event: `${eventPrefix}${CUSTOMER_IO_EVENT.PRICES_SELECTED}`,
    properties: {
      prices: selectedCategories.map((category) => ({
        product: category.selectedProduct?.name,
        category: CUSTOMER_IO_CATEGORY_SLUG[category.slug],
        price: category.selectedProduct?.selectedPrice?.price,
      })),
      source: window.location.href,
    },
    userId,
  });
};

interface CustomerIoCheckoutPageViewed {
  user: OnboardingUser;
  categories: CategoryWithSelectedProduct[];
  eventPrefix?: EventPrefix;
}

export const triggerCustomerIoCheckoutPageViewed = ({
  user,
  categories,
  eventPrefix = EVENT_PREFIX.NONE,
}: CustomerIoCheckoutPageViewed) => {
  const products = categoriesToCustomerIoProducts(categories);
  const totalPrice = products.reduce((acc, product) => acc + product.price, 0);

  trackPublicEvent({
    event: `${eventPrefix}${CUSTOMER_IO_EVENT.CHECKOUT_PAGE_VIEWED}`,
    properties: {
      products,
      totalPrice,
    },
    userId: user.email,
  });
};

interface FailedCustomerIoOnboardingPaymentResultParams {
  user: OnboardingUser;
  categories: CategoryWithSelectedProduct[];
  checkoutDiscount?: CheckoutDiscount;
  eventPrefix?: EventPrefix;
}

export const triggerCustomerIoOnboardingPaymentFailed = ({
  user,
  categories,
  checkoutDiscount,
  eventPrefix = EVENT_PREFIX.NONE,
}: FailedCustomerIoOnboardingPaymentResultParams) => {
  const finalPrice = checkoutDiscount?.isValid
    ? checkoutDiscount.totalAfterDiscount
    : calculateTotal(categories);
  const products = categoriesToCustomerIoProducts(categories);

  trackPublicEvent({
    event: `${eventPrefix}${CUSTOMER_IO_EVENT.PAYMENT_FAILED}`,
    properties: {
      products,
      totalPrice: finalPrice,
      discountCode: checkoutDiscount?.code,
      source: window.location.href,
    },
    userId: user.email,
  });
};

interface CompletedCustomerIoOnboardingPaymentResultParams
  extends FailedCustomerIoOnboardingPaymentResultParams {
  metadata: UrlParamsSlice["urlParams"];
  succeededPaymentIds?: string[];
}

export const triggerCustomerIoOnboardingPaymentCompleted = ({
  user,
  categories,
  checkoutDiscount,
  metadata,
  succeededPaymentIds,
  eventPrefix = EVENT_PREFIX.NONE,
}: CompletedCustomerIoOnboardingPaymentResultParams) => {
  const finalPrice = checkoutDiscount?.isValid
    ? checkoutDiscount.totalAfterDiscount
    : calculateTotal(categories);
  const products = categoriesToCustomerIoProducts(categories);

  trackPublicEvent({
    event: `${eventPrefix}${CUSTOMER_IO_EVENT.PAYMENT_COMPLETED}`,
    properties: {
      succeededPaymentIds,
      amountPaid: finalPrice,
      discountCode: checkoutDiscount?.code,
      products,
      hasWeightLossProducts: hasWeightLossProduct(products),
      metadata: removeEmptyValues(metadata),
      source: window.location.href,
    },
    userId: user.email,
  });

  const previousCustomerIoId = metadata[ANALYTICS_PARAMS.CUSTOMER_IO_ID];

  if (previousCustomerIoId) {
    try {
      void mergeCustomerIoProfiles({
        userCode: user.code,
        previousCustomerIoId,
      });
    } catch (error) {
      handleAnalyticsError(error, {
        message: "Error merging Customer.io profiles",
        provider: ANALYTICS_PROVIDER.CUSTOMER_IO,
        extra: {
          userCode: user.code,
          previousCustomerIoId,
        },
      });
    }
  }
};
